import { Clock, MapPin } from "lucide-react";
import clsx from "clsx";
import type { CoachAssignment } from "@flexv-coach/shared";
import { Badge } from "@/components/ui/Badge";
import { formatTimeRange } from "@/lib/date";
import { roleLabel, sourceLabel, statusAccent, statusLabel, statusTone } from "@/lib/labels";

interface AssignmentCardProps {
    assignment: CoachAssignment;
    onSelect: (assignment: CoachAssignment) => void;
    compact?: boolean;
}

export function AssignmentCard({ assignment, onSelect, compact = false }: AssignmentCardProps) {
    return (
        <button
            type="button"
            onClick={() => onSelect(assignment)}
            className={clsx(
                "group w-full overflow-hidden rounded-xl border text-left transition-all",
                "app-content-card app-shell-border hover:shadow-md",
            )}
        >
            <div className="flex">
                <div className={clsx("w-1 shrink-0", statusAccent(assignment.status))} />
                <div className={clsx("min-w-0 flex-1", compact ? "p-2.5" : "p-3")}>
                    <div className="flex items-start justify-between gap-2">
                        <p className="app-text-primary truncate text-sm font-semibold">
                            {assignment.sourceName ?? assignment.sourceType}
                        </p>
                        <Badge tone={statusTone(assignment.status)} className="shrink-0">
                            {statusLabel(assignment.status)}
                        </Badge>
                    </div>

                    <div className="app-shell-text-muted mt-2 flex flex-col gap-1 text-xs">
                        <span className="inline-flex items-center gap-1.5">
                            <Clock className="h-3.5 w-3.5" />
                            {formatTimeRange(assignment.startTime, assignment.endTime)}
                        </span>
                        <span className="inline-flex items-center gap-1.5 truncate">
                            <MapPin className="h-3.5 w-3.5 shrink-0" />
                            <span className="truncate">{assignment.outlet?.name ?? "—"}</span>
                        </span>
                    </div>

                    {!compact && (
                        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                            <span className="app-content-subtle app-shell-text-muted rounded-md px-2 py-0.5 text-[11px] font-medium">
                                {roleLabel(assignment.role)}
                            </span>
                            {assignment.sport && (
                                <span className="app-content-subtle app-shell-text-muted rounded-md px-2 py-0.5 text-[11px] font-medium">
                                    {assignment.sport.name}
                                </span>
                            )}
                            <span className="app-text-soft text-[11px]">
                                {sourceLabel(assignment.sourceType)}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </button>
    );
}
